'use client'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import './home.css';

import {NotificationObj} from '@/src/components/notification';
import {LoaderService} from '@/src/services/loader';
import {LoggerService} from '@/src/services/logger';
import {BehaviorSubject, Subscription} from "rxjs";
import {Helper} from "@/src/lib/helper";
import {ConfigProvider, Layout,} from "antd";
import {Content} from "antd/lib/layout/layout";
import {DataViewModel} from "@/src/viewModels/dataViewModel";
import TweenOne from 'rc-tween-one';
import Children from 'rc-tween-one/lib/plugin/ChildrenPlugin';
import {MediaQueryEnum} from "@ant-design/pro-utils/lib/useMediaQuery";
import CarouselComponent from "@/src/app/[locale]/home/components/carousel";
import ContactComponent from "@/src/app/[locale]/home/components/contact";
import CompanyComponent from "@/src/app/[locale]/home/components/company";
// import {useRouter, usePathname} from "@/src/i18n/routing";
import AnimatedStatsComponent from "@/src/app/[locale]/home/components/animatedStats";
import {Faq} from "@/src/entities/faq";
import FaqComponent from "@/src/app/[locale]/home/components/faq";
import {LanguageEnum} from "@/src/entities/languageEnum";
import {Review} from "@/src/entities/review";
import ReviewComponent from "@/src/app/[locale]/home/components/review";
import VideoComponent from "@/src/app/[locale]/home/components/video";
import OrganizationsComponent from "@/src/app/[locale]/home/components/organizations";
import ServicesComponent from "@/src/app/[locale]/home/components/services";
import CookieBanner from "@/src/app/[locale]/home/components/cookieBanner";
import {Constants} from "@/src/lib/constants";
import {UserPreferences} from "@/src/entities/UserPreferences";
import {ThemeService} from "@/src/services/theme";
import LodgingsComponent from "@/src/app/[locale]/home/components/lodgings";
import {Lodging} from "@/src/entities/lodging";
import {useRouter, usePathname} from "@/src/i18n/routing";

const HomePage = () => {
    const localization = useTranslations('home');
    const generalLocalization = useTranslations('general');
    const themeColors = ThemeService.getThemeColors()
    const [notificationState, setNotificationState] = useState<NotificationObj | null>(null);

    const [isLargeScreen, setIsLargeScreen] = useState<boolean>(true)

    const viewModel = new DataViewModel();

    let faqSubscription: Subscription | null;
    let reviewSubscription: Subscription | null;
    let lodgingSubscription: Subscription | null;

    const loaderService = LoaderService.getInstance();
    const [userPreferences, setUserPreferences] = useState<String | null>(null)
    const locale = useLocale();
    const [faqList, setFaqList] = useState<Faq[]>([])
    const [reviewList, setReviewList] = useState<Review[]>([])
    const [lodgingList, setLodgingList] = useState<Lodging[]>([])

    const [selectedTab, setSelectedTab] = useState("photovoltaicTab")
    const router: any = useRouter()
    const reviewRef = useRef<null | HTMLElement>(null)
    const lodgingsRef = useRef<null | HTMLElement>(null)
    const companyRef = useRef<null | HTMLElement>(null)
    const contactRef = useRef<null | HTMLElement>(null)
    const faqRef = useRef<null | HTMLElement>(null)
    const servicesRef = useRef<null | HTMLElement>(null)

    const pathname = usePathname()

    const loaderVisibilityCheckers = new BehaviorSubject([
        {id: "page", loaded: false},
        {id: "faqSubscription", loaded: false},
        {id: "reviewSubscription", loaded: false},
        {id: "lodgingSubscription", loaded: false}
    ])

    useEffect(() => {
        const page = document.location.href.slice(
            document.location.href.lastIndexOf("/") + 1,
            document.location.href.length
        )
        LoggerService.getLogger().debug("<<< pathname:" + page)
        switch (page) {
            case "home#company":
                goToSection(companyRef)
                break
            case "home#services":
                goToSection(servicesRef)
                break
            case "home#lodgings":
                goToSection(lodgingsRef)
                break
            case "home#reviews":
                goToSection(reviewRef)
                break
            case "home#contacts":
                goToSection(contactRef)
                break
            default: break
        }
        // if (pathname.indexOf("#reviews") > 0) {
        //     goToSection(reviewRef)
        // } else if (pathname.indexOf("#contacts") > 0) {
        //     goToSection(contactRef)
        // }
    }, [pathname]);

    const goToSection = (ref: React.MutableRefObject<null | HTMLElement>) => {
            if (ref.current!==null) {
                ref.current?.scrollIntoView({behavior: "smooth"})
            }
    }

    useEffect(() => {
        TweenOne.plugins.push(Children);
        setUserPreferences(window.localStorage.getItem(Constants.userPrivacyPrefsKey))
        loaderService.show()

        let language: LanguageEnum
        switch (locale) {
            case "it":
                language = LanguageEnum.ITALIAN
                break
            case "en":
                language = LanguageEnum.ENGLISH
                break
            default:
                language = LanguageEnum.ENGLISH
                break
        }
        LoggerService.getLogger().debug(`<<< language: ${locale}`)
        faqSubscription = viewModel.retrieveFaqList(language).subscribe({
            next: value => {
                LoggerService.getLogger().debug(`<<< FAQ: ${JSON.stringify(value)}`)
                const checker = loaderVisibilityCheckers.value.map(value => {
                    if (value.id === "faqSubscription") {
                        value.loaded = true
                    }
                    return value
                })
                loaderVisibilityCheckers.next(checker)
                setFaqList(value)
            },
            error: error => {
                LoggerService.getLogger().debug(`error: ${JSON.stringify(error)}`)

                const parsedError = JSON.parse(JSON.stringify(error))
                const errorCode = error.error.statusCode;
                const errorMessage = error.error.message;

                const notificationObj = {
                    type: "error",
                    title: generalLocalization("error"),
                    description: errorMessage
                }
                setNotificationState(notificationObj)
            }
        })

        reviewSubscription = viewModel.retrieveReviewList(language).subscribe({
            next: value => {
                const checker = loaderVisibilityCheckers.value.map(value => {
                    if (value.id === "reviewSubscription") {
                        value.loaded = true
                    }
                    return value
                })
                loaderVisibilityCheckers.next(checker)
                setReviewList(value)
            },
            error: error => {
                loaderService.hide()
                LoggerService.getLogger().debug(`error: ${JSON.stringify(error)}`)

                const parsedError = JSON.parse(JSON.stringify(error))
                const errorCode = error.error.statusCode;
                const errorMessage = error.error.message;

                const notificationObj = {
                    type: "error",
                    title: generalLocalization("error"),
                    description: errorMessage
                }
                setNotificationState(notificationObj)
            }
        })

        lodgingSubscription = viewModel.retrieveLodgingList(language).subscribe({
            next: value => {
                const checker = loaderVisibilityCheckers.value.map(value => {
                    if (value.id === "lodgingSubscription") {
                        value.loaded = true
                    }
                    return value
                })
                loaderVisibilityCheckers.next(checker)
                setLodgingList(value)
            },
            error: error => {
                loaderService.hide()
                LoggerService.getLogger().debug(`error: ${JSON.stringify(error)}`)

                const parsedError = JSON.parse(JSON.stringify(error))
                const errorCode = error.error.statusCode;
                const errorMessage = error.error.message;

                const notificationObj = {
                    type: "error",
                    title: generalLocalization("error"),
                    description: errorMessage
                }
                setNotificationState(notificationObj)
            }
        })

        const checker = loaderVisibilityCheckers.value.map(value => {
            if (value.id === "page") {
                value.loaded = true
            }
            return value
        })
        loaderVisibilityCheckers.next(checker)

        loaderVisibilityCheckers.subscribe(checkerList => {
            LoggerService.getLogger().debug(`<<< loading checker: ${JSON.stringify(checkerList)}`)
            const boolValues = checkerList.map(value => {
                return value.loaded
            })
            if (boolValues.indexOf(false) < 0) {
                loaderService.hide()
            }
        })

        window.addEventListener("resize", handleResize);
        setIsLargeScreen(Helper.checkLargeScreen())
        LoggerService.getLogger().debug(`<<< lg: ${MediaQueryEnum.lg.minWidth}`)
        function handleResize() {
            setIsLargeScreen(Helper.checkLargeScreen())
            // setDrawerOpened(false)
        }

        return () => {
            unsubscribeSubscription(faqSubscription)
            unsubscribeSubscription(reviewSubscription)
            unsubscribeSubscription(lodgingSubscription)

            window.removeEventListener("resize", handleResize)
        };

    }, []);
    const startGoogleAnalitycs = (prefs: UserPreferences) => {
        if (prefs.statistics) {

        }
    }
    const unsubscribeSubscription = (subscription: Subscription | null) => {
        if (subscription != null) {
            subscription.unsubscribe();
            subscription = null
            LoggerService.getLogger().debug(`<<< UNSUSCRIBE ${subscription} SERVICE`)
        }
    }

    return (
        <ConfigProvider
            theme={{
                components: {

                }
            }}
        >
            <Layout>
                <Content style={{ backgroundColor: `${themeColors.tertiary}` }}>

                    <CarouselComponent />

                    <section id="company" ref={companyRef} style={{backgroundColor: themeColors.containersBackgroundEven}}>
                        <CompanyComponent selectedTab={selectedTab} onSelectTab={(activeKey) => {
                            setSelectedTab(activeKey)
                        }} />
                    </section>
                    <section id="video">
                        <VideoComponent />
                    </section>

                    <section id="lodgings" ref={lodgingsRef} style={{backgroundColor: themeColors.containersBackgroundOdd}}>
                        <LodgingsComponent lodgingList={lodgingList} />
                    </section>

                    <section id="stats" style={{backgroundColor: themeColors.containersBackgroundEven, padding: "40px 0"}}>
                        <AnimatedStatsComponent />
                    </section>

                    <section id="services" ref={servicesRef} style={{backgroundColor: themeColors.containersBackgroundOdd}}>
                        <ServicesComponent />
                    </section>

                    <section id="reviews" ref={reviewRef} style={{backgroundColor: themeColors.containersBackgroundEven}}>
                        <ReviewComponent reviewList={reviewList} />
                    </section>

                    <section id="faq" ref={faqRef} style={{backgroundColor: themeColors.containersBackgroundOdd}}>
                        <FaqComponent faqList={faqList} />
                    </section>

                    <section id="contacts" ref={contactRef} style={{backgroundColor: themeColors.containersBackgroundEven}}>
                        <ContactComponent />
                    </section>

                    <section id="organanizations">
                        <OrganizationsComponent />
                    </section>


                    {(() => {
                        if (userPreferences === null) {
                            return <CookieBanner onUserPropertySent={prefs => {
                                LoggerService.getLogger().debug(`<<< USER PREFS: ${JSON.stringify(prefs)}`)
                                setUserPreferences(JSON.stringify(prefs))
                            }}/>
                        }
                    })()}
                </Content>

            </Layout>
        </ConfigProvider>
    );
};

export default HomePage;